import type { ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Info, Star } from 'lucide-react';

/** One of the open card's blocks, rising in after the card has opened, a beat after the one above it (`app-reveal`). */
function Staggered(props: { index: number; children: ReactNode }) {
	return (
		<div className="app-reveal" style={{ animationDelay: `${props.index * 0.12}s` }}>
			{props.children}
		</div>
	);
}

function GoalCard() {
	const { t } = useLingui();
	return (
		<div className="flex flex-col gap-2.5 rounded-2xl bg-white px-4 py-3 text-[#1d1d1b] shadow-[0_12px_24px_0_rgba(77,33,0,0.08)]">
			<span className="flex items-center gap-2.5">
				<span className="grid size-8 shrink-0 place-items-center rounded-full bg-[#ff7e1d]">
					<Star className="size-4 fill-white text-white" />
				</span>
				<span className="flex min-w-0 flex-col">
					<span className="text-[15px] leading-tight tracking-[-0.2px]">{t`Annual Goal Progress`}</span>
					<span className="text-[11px] text-[#676b43]">{t`You're getting closer to your goal`}</span>
				</span>
			</span>
			<span className="relative h-1.5 overflow-hidden rounded-full bg-[#b0b86a]/20">
				<span className="app-progress absolute inset-y-0 left-0 w-[62%] origin-left rounded-full bg-[#b0b86a]" />
			</span>
			<span className="flex items-center gap-1 text-[10px] text-[#1d1d1b]/60 italic">
				<Info className="size-3 shrink-0" />
				{t`Your tax deduction is included!`}
			</span>
		</div>
	);
}

function CategoriesCard() {
	const { t } = useLingui();
	// The donors app's first three chart colours. Each slice starts where the ones before it end.
	const slices = [
		{ share: 45, start: 0, color: 'oklch(69.06% 0.20235 36.131)', label: t`Health` },
		{ share: 30, start: 45, color: 'oklch(88.353% 0.08872 72.834)', label: t`Childhood` },
		{ share: 25, start: 75, color: 'oklch(64.229% 0.04155 100.968)', label: t`Environment` },
	];
	return (
		<div className="flex items-center justify-between gap-2 rounded-3xl bg-white py-3 pr-3 pl-4 text-[#1d1d1b]">
			<span className="flex flex-col gap-2">
				<span className="max-w-[140px] text-[14px] leading-snug font-semibold">{t`See where your support goes`}</span>
				<ul className="flex flex-col gap-1">
					{slices.map((slice) => (
						<li key={slice.label} className="flex items-center gap-1.5 text-[10px]">
							<span className="size-2.5 shrink-0 rounded-[3px]" style={{ backgroundColor: slice.color }} />
							<span className="font-semibold">{slice.share}%</span>
							<span className="font-medium">{slice.label}</span>
						</li>
					))}
				</ul>
			</span>
			{/* A gap of 1.5 of the 100 keeps the slices apart; the largest one stands proud. */}
			<svg viewBox="0 0 100 100" aria-hidden className="app-donut size-[92px] shrink-0 -rotate-90">
				{slices.map((slice, index) => (
					<circle
						key={slice.label}
						cx="50"
						cy="50"
						r="34"
						fill="none"
						pathLength={100}
						stroke={slice.color}
						strokeWidth={index === 0 ? 20 : 14}
						strokeDasharray={`${slice.share - 1.5} ${100 - slice.share + 1.5}`}
						strokeDashoffset={-slice.start}
					/>
				))}
			</svg>
		</div>
	);
}

function TopList(props: { title: string; items: string[] }) {
	return (
		<div className="flex min-w-0 flex-1 flex-col gap-2 rounded-2xl bg-white p-3 text-[#1d1d1b]">
			<span className="text-[12px] font-semibold">{props.title}</span>
			<ol className="flex flex-col gap-1">
				{props.items.map((item, index) => (
					<li key={item} className="flex min-w-0 items-center gap-1.5">
						<span className="grid size-4 shrink-0 place-items-center rounded-[4px] bg-[#929071] text-[9px] text-white">
							{index + 1}
						</span>
						<span className="truncate text-[11px]">{item}</span>
					</li>
				))}
			</ol>
		</div>
	);
}

/** What the orange card holds once open: the annual goal, the categories, and the top three NGOs and campaigns. */
export function AppHomeDetails() {
	const { t } = useLingui();
	return (
		<>
			<Staggered index={0}>
				<GoalCard />
			</Staggered>
			<Staggered index={1}>
				<CategoriesCard />
			</Staggered>
			<Staggered index={2}>
				<div className="flex gap-3">
					<TopList title={t`Top 3 NGOs`} items={['UNICEF', 'WWF', t`Red Cross`]} />
					<TopList title={t`Top 3 campaigns`} items={[t`School meals`, t`Help refugees`, t`Clean water`]} />
				</div>
			</Staggered>
		</>
	);
}
